import React from 'react';
import {
    Typography,
    Button,
    useTheme,
    Box,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled, alpha } from '@mui/material/styles';

const StyledPreviewZone = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'default' }) => {
    const isDefault = variant === 'default';
    const isDropzone = variant === 'dropzone';

    return {
        width: '100%',
        minHeight: theme.spacing(30),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        border: isDropzone
            ? `2px dashed ${theme.palette.divider}`
            : 'none',
        borderRadius: theme.spacing(1),
        backgroundColor: 'transparent',
        position: 'relative',
        overflow: 'hidden',
    };
});

const PreviewImageContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: theme.spacing(25),
    maxHeight: theme.spacing(40),
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
}));

const PreviewImage = styled('img')({
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
});

const RemoveButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: alpha(theme.palette.error.main, 0.9),
    color: theme.palette.error.contrastText,
    '&:hover': {
        backgroundColor: theme.palette.error.main,
    },
    zIndex: 10,
}));

const FileInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    width: '100%',
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

/**
 * Preview File Zone Component
 * 
 * Displays a preview of the selected file with options to remove or change it.
 * Similar structure to FileDropZone but for displaying selected files.
 * 
 * @param {Object} props
 * @param {'default'|'dropzone'} props.variant - Preview zone variant (default: 'default')
 * @param {string} props.preview - Preview image/data URL
 * @param {string} props.fileName - Name of the file
 * @param {string|number} props.fileSize - Size of the file (bytes or formatted string)
 * @param {Function} props.onRemove - Callback when remove button is clicked
 * @param {Function} props.onChange - Optional callback when change button is clicked
 * @param {boolean} props.showChangeButton - Show "Change Image" button (default: false)
 * @param {string} props.alt - Alt text for preview image
 * @param {Object} props.sx - Additional MUI sx styles
 */
export const PreviewFileZone = ({
    variant = 'default',
    preview,
    fileName,
    fileSize,
    onRemove,
    onChange,
    showChangeButton = false,
    alt = 'Preview',
    sx,
    ...otherProps
}) => {
    const theme = useTheme();

    // Format file size if it's a number
    const formatFileSize = (size) => {
        if (typeof size === 'string') return size;
        if (typeof size !== 'number') return '';

        const units = ['B', 'KB', 'MB', 'GB'];
        let unitIndex = 0;
        let fileSize = size;

        while (fileSize >= 1024 && unitIndex < units.length - 1) {
            fileSize /= 1024;
            unitIndex++;
        }

        return `${Math.round(fileSize * 100) / 100} ${units[unitIndex]}`;
    };

    const displayFileSize = formatFileSize(fileSize);

    return (
        <StyledPreviewZone
            variant={variant}
            sx={sx}
            {...otherProps}
        >
            <PreviewImageContainer>
                <PreviewImage
                    src={preview}
                    alt={alt}
                />
                {onRemove && (
                    <RemoveButton
                        size="small"
                        onClick={onRemove}
                        aria-label="Remove file"
                    >
                        <CloseIcon fontSize="small" />
                    </RemoveButton>
                )}
            </PreviewImageContainer>

            <FileInfo>
                <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
                    {fileName}
                </Typography>
                {displayFileSize && (
                    <Typography variant="caption" color="text.secondary">
                        {displayFileSize}
                    </Typography>
                )}
            </FileInfo>

            {showChangeButton && onChange && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={onChange}
                    sx={{ mt: 1 }}
                >
                    Change Image
                </Button>
            )}
        </StyledPreviewZone>
    );
};

